import React, { useCallback, useEffect, memo, useRef, useState, type ReactNode } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { Text } from '@atlaskit/primitives';
import { type FlagId, toFlagId, useFlagsService } from '@atlassian/jira-flags';
import HelpPanelLink from '@atlassian/jira-help-panel-button';
import InspectCode from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/inspect-code';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useJiraServiceManagementProjectNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { servicedeskPracticesResource } from '@atlassian/jira-router-resources-service-desk-practices/src/controllers/index.tsx';
import {
	CSM_CUSTOMER_CONTEXT_FEATURE,
	CSM_DEVELOPER_ESCALATIONS_FEATURE,
	CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE,
	ENABLED,
} from '@atlassian/jira-servicedesk-common/src/service-project-features/constants';
import { useIsJCS } from '@atlassian/jira-servicedesk-customer-service-common/src/utils/use-is-jcs';
import { useCachedEscalationsRequestTypeActions } from '@atlassian/jira-servicedesk-customer-service-escalations-issue-view-button/src/controllers/use-cached-escalations-request-type/index.tsx';
import { SHORT_FORM_DEVELOPER_ESCALATION } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { usePathParam, useRouterActions, useResource } from '@atlassian/react-resource-router';
import { FeatureToggleTile } from '../../../../../common/ui/feature-toggle-tile/index.tsx';
import useAffectedRequestTypes from '../../../../../controllers/affected-request-types';
import useSaveFailedFlag from '../../../../../controllers/common/save-failed-flag/index.tsx';
import useServiceProjectFeaturesController from '../../../../../controllers/common/service-project-features-controller/index.tsx';
import { AsyncConfirmDisableModal } from './confirm-disable-modal/async';
import CustomerContextFeatureTile from './customer-context';
import messages from './messages';
import ProductsAndEntitlementsFeatureTile from './products-and-entitlements-tile';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CsmFeatures = memo<Record<any, any>>(() => {
	const { formatMessage } = useIntl();
	const { data, saving, update } = useServiceProjectFeaturesController();
	const showSaveErrorFlag = useSaveFailedFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag, dismissFlag } = useFlagsService();
	const { push } = useRouterActions();
	const isJCS = useIsJCS();

	const { refresh: refreshPractices } = useResource(servicedeskPracticesResource);
	const { refresh: refreshJSMNavigation } = useJiraServiceManagementProjectNavigation();
	const { flush: flushCachedEscalationsRequestType } = useCachedEscalationsRequestTypeActions();

	const [projectKey = ''] = usePathParam('projectKey');
	const flagIdRef = useRef<{ [key: string]: FlagId }>({});

	const {
		data: { numAffectedRequestTypes: numDeveloperEscalationsAffectedRequestTypes },
		error: developerEscalationsAffectedRequestTypesError,
		fetch: fetchDeveloperEscalationsRequestTypes,
	} = useAffectedRequestTypes(CSM_DEVELOPER_ESCALATIONS_FEATURE);

	const [isDisablingDeveloperEscalations, setDisablingDeveloperEscalations] = useState(false);
	const [isDisableDeveloperEscalationsModalOpen, setIsDisableDeveloperEscalationsModalOpen] =
		useState(false);

	const isJiraAdmin = useIsAdmin();

	const csmDeveloperEscalationsInProductHelpArticleId = '3qm63PXTQWey7cMEvrEtyk';
	const csmDeveloperEscalationsFallbackArticleUrl =
		'https://support.atlassian.com/jira-service-management-cloud/docs/manage-your-developer-escalations/';
	const csmDeveloperEscalationsInProductHelpLink = (
		<HelpPanelLink
			appearance="link"
			spacing="none"
			articleId={csmDeveloperEscalationsInProductHelpArticleId}
			fallbackComponent={
				<a target="_blank" href={csmDeveloperEscalationsFallbackArticleUrl}>
					{formatMessage(messages.csmDeveloperEscalationsLearnMore)}
				</a>
			}
			onClick={() =>
				fireUIAnalytics(
					createAnalyticsEvent({}),
					'helpLink clicked',
					'learnMoreAboutCsmDeveloperEscalations',
					{
						csmDeveloperEscalationsInProductHelpArticleId,
					},
				)
			}
		>
			{formatMessage(messages.csmDeveloperEscalationsLearnMore)}
		</HelpPanelLink>
	);

	const updateCsmDeveloperEscalations = useCallback(
		(enabled: boolean) => {
			update({
				feature: CSM_DEVELOPER_ESCALATIONS_FEATURE,
				enabled,
				onSuccess: () => {
					const flagIdKey = `${CSM_DEVELOPER_ESCALATIONS_FEATURE}-service-feature-success`;
					const { current: flagIds } = flagIdRef;
					// toFlagId creates a new string reference. But we want to reuse
					// flag ids so the most relevant flag for a feature is shown
					flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);

					const id = flagIds[flagIdKey];
					const onClick = () => {
						dismissFlag(id);
					};
					// Dismiss previous flag with the same id. So user cannot
					// interact with out of update flags
					dismissFlag(id);

					refreshPractices();
					refreshJSMNavigation();
					flushCachedEscalationsRequestType();

					showFlag({
						id,
						type: 'success',
						isAutoDismiss: !enabled,
						title: [messages.csmDeveloperEscalationsContactSuccessTitle, { value: enabled }],
						description: [
							enabled
								? messages.csmDeveloperEscalationsSuccessEnableDescription
								: messages.csmDeveloperEscalationSuccessDisableDescription,
							{
								strong: (children: ReactNode) => <Text weight="bold">{children}</Text>,
							},
						],
						actions: enabled
							? [
									{
										content: messages.csmDeveloperEscalationsSuccessEnableDismiss,
										onClick,
									},
									{
										content: messages.csmDeveloperEscalationsSuccessEnableAction,
										onClick: () => {
											dismissFlag(id);
											push(
												`/jira/servicedesk/projects/${projectKey}/settings/request-types/category/${SHORT_FORM_DEVELOPER_ESCALATION}`,
											);
										},
									},
								]
							: [
									{
										content: messages.csmDeveloperEscalationsSuccessDisableDismiss,
										onClick,
									},
								],
					});
				},
				onFail: () => {
					showSaveErrorFlag(formatMessage(messages.csmDeveloperEscalationsTitle), enabled);
				},
			});
		},
		[
			dismissFlag,
			projectKey,
			push,
			refreshJSMNavigation,
			refreshPractices,
			showFlag,
			showSaveErrorFlag,
			update,
			formatMessage,
			flushCachedEscalationsRequestType,
		],
	);

	// This hook is used to ensure that fetchDeveloperEscalationsRequestTypes() called in disable()
	// fetches all requisite data before determining whether to show the warning modal.
	useEffect(() => {
		if (
			(numDeveloperEscalationsAffectedRequestTypes !== undefined &&
				numDeveloperEscalationsAffectedRequestTypes > 0) ||
			developerEscalationsAffectedRequestTypesError
		) {
			setIsDisableDeveloperEscalationsModalOpen(true);
		} else if (numDeveloperEscalationsAffectedRequestTypes === 0) {
			updateCsmDeveloperEscalations(false);
			setDisablingDeveloperEscalations(false);
		}
	}, [
		developerEscalationsAffectedRequestTypesError,
		numDeveloperEscalationsAffectedRequestTypes,
		updateCsmDeveloperEscalations,
	]);

	const disable = useCallback(() => {
		setDisablingDeveloperEscalations(true);
		fetchDeveloperEscalationsRequestTypes();
	}, [setDisablingDeveloperEscalations, fetchDeveloperEscalationsRequestTypes]);

	const createCsmDeveloperEscalationsOnChangeHandler = useCallback(
		() => (feature: string, enabled: boolean, analyticEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticEvent, 'settingsToggle', { field: feature });

			if (enabled) {
				updateCsmDeveloperEscalations(enabled);
			} else {
				disable();
			}
		},
		[disable, updateCsmDeveloperEscalations],
	);

	const onConfirmDisable = useCallback(() => {
		updateCsmDeveloperEscalations(false);
		setIsDisableDeveloperEscalationsModalOpen(false);
		setDisablingDeveloperEscalations(false);
	}, [updateCsmDeveloperEscalations]);

	const closeDisableModal = useCallback(() => {
		setIsDisableDeveloperEscalationsModalOpen(false);
		setDisablingDeveloperEscalations(false);
	}, []);

	return (
		<>
			{data?.[CSM_CUSTOMER_CONTEXT_FEATURE] != null && (
				<CustomerContextFeatureTile
					isJiraAdmin={isJiraAdmin}
					isProductsAndEntitlementsEnabled={
						data?.[CSM_PRODUCT_AND_ENTITLEMENTS_FEATURE]?.status === ENABLED
					}
				/>
			)}
			<ProductsAndEntitlementsFeatureTile
				isJiraAdmin={isJiraAdmin}
				isCustomerContextEnabled={data?.[CSM_CUSTOMER_CONTEXT_FEATURE]?.status === ENABLED}
			/>
			{!isJCS && (
				<FeatureToggleTile
					feature={CSM_DEVELOPER_ESCALATIONS_FEATURE}
					title={formatMessage(messages.csmDeveloperEscalationsTitle)}
					illustration={<InspectCode alt="" />}
					value={data?.[CSM_DEVELOPER_ESCALATIONS_FEATURE]?.status === ENABLED}
					saving={saving[CSM_DEVELOPER_ESCALATIONS_FEATURE] || isDisablingDeveloperEscalations}
					onChange={createCsmDeveloperEscalationsOnChangeHandler()}
					disabled={!isJiraAdmin}
					tooltip={isJiraAdmin ? null : formatMessage(messages.csmCustomerContextContactAdmin)}
				>
					<FormattedMessage
						{...messages.csmDeveloperEscalationsDescription}
						values={{
							learnMoreLink: csmDeveloperEscalationsInProductHelpLink,
						}}
					/>
				</FeatureToggleTile>
			)}
			{isDisableDeveloperEscalationsModalOpen && (
				<AsyncConfirmDisableModal
					feature={CSM_DEVELOPER_ESCALATIONS_FEATURE}
					onClose={closeDisableModal}
					onConfirm={onConfirmDisable}
					numAffectedRequestTypes={numDeveloperEscalationsAffectedRequestTypes}
					affectedRequestTypesError={developerEscalationsAffectedRequestTypesError}
				/>
			)}
		</>
	);
});

export default CsmFeatures;
